import { useEffect } from 'react';

/** Maximum tilt in degrees (the plan's E1: ≤ 4°). */
export const TILT_MAX_DEG = 4;

const FINE_POINTER = '(hover: hover) and (pointer: fine)';
const REDUCED = '(prefers-reduced-motion: reduce)';

const matches = (query) => {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && Boolean(window.matchMedia(query)?.matches);
  } catch {
    return false;
  }
};

/** Tilt is only for a real mouse or trackpad, and never with reduced motion. */
export const canTilt = () => matches(FINE_POINTER) && !matches(REDUCED);

/**
 * Pointer position inside a box → tilt and light position.
 * x/y are 0..1 from the top-left. The card leans away from the pointer
 * (rotateY follows x, rotateX follows y). In RTL the horizontal lean is
 * mirrored so the light falls from the reading start.
 */
export const tiltFor = (x, y, rtl = false, max = TILT_MAX_DEG) => {
  const cx = Math.min(1, Math.max(0, x)) - 0.5;
  const cy = Math.min(1, Math.max(0, y)) - 0.5;
  const ry = (rtl ? -cx : cx) * 2 * max;
  const rx = -cy * 2 * max;
  const round = (n) => Math.round(n * 100) / 100 || 0;
  return { rx: round(rx), ry: round(ry), mx: Math.round(Math.min(1, Math.max(0, x)) * 100), my: Math.round(Math.min(1, Math.max(0, y)) * 100) };
};

/**
 * 3D tilt + light sheen for a card's image frame (premium E1).
 *
 * Writes four CSS variables on `ref.current` (--tilt-rx, --tilt-ry, --tilt-mx,
 * --tilt-my) at most once per animation frame, and `data-tilting` while the
 * pointer is over it; `styles/premium.css` turns them into a
 * perspective transform and a soft-light sheen. Nothing re-renders.
 *
 * Only attached on devices that really hover (mouse / trackpad) and when the
 * shopper has not asked for reduced motion; touch gets no tilt at all.
 */
const useTilt = (ref, { rtl = false, disabled = false } = {}) => {
  useEffect(() => {
    const node = ref.current;
    if (!node || disabled || !canTilt()) return undefined;

    let frame = 0;
    let pending = null;
    const raf = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : (cb) => setTimeout(cb, 16);
    const caf = typeof window.cancelAnimationFrame === 'function'
      ? window.cancelAnimationFrame.bind(window)
      : clearTimeout;

    const apply = () => {
      frame = 0;
      if (!pending) return;
      const { rx, ry, mx, my } = pending;
      node.style.setProperty('--tilt-rx', `${rx}deg`);
      node.style.setProperty('--tilt-ry', `${ry}deg`);
      node.style.setProperty('--tilt-mx', `${mx}%`);
      node.style.setProperty('--tilt-my', `${my}%`);
    };

    const onMove = (event) => {
      if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      const rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      pending = tiltFor((event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height, rtl);
      if (!frame) frame = raf(apply);
    };
    const onEnter = (event) => {
      if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      node.setAttribute('data-tilting', 'true');
      onMove(event);
    };
    const onLeave = () => {
      if (frame) caf(frame);
      frame = 0;
      pending = null;
      node.removeAttribute('data-tilting');
      ['--tilt-rx', '--tilt-ry', '--tilt-mx', '--tilt-my'].forEach((name) => node.style.removeProperty(name));
    };

    node.addEventListener('pointerenter', onEnter);
    node.addEventListener('pointermove', onMove);
    node.addEventListener('pointerleave', onLeave);
    return () => {
      onLeave();
      node.removeEventListener('pointerenter', onEnter);
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerleave', onLeave);
    };
  }, [ref, rtl, disabled]);
};

export default useTilt;
